import axios from 'axios';

// Backend API base URL - always use relative path for deployed version
const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Agent API functions
export const agentAPI = {
  // Get all agents
  getAll: async () => {
    try {
      const response = await api.get('/agents');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      throw error;
    }
  },

  // Get specific agent
  getById: async (agentId) => {
    try {
      const response = await api.get(`/agents/${agentId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch agent ${agentId}:`, error);
      throw error;
    }
  },

  // Create new agent
  create: async (agentData) => {
    try {
      const response = await api.post('/agents', agentData);
      return response.data;
    } catch (error) {
      console.error('Failed to create agent:', error);
      throw error;
    }
  },

  // Get agent facts
  getFacts: async (agentId) => {
    try {
      const response = await api.get(`/agents/${agentId}/facts`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch facts for ${agentId}:`, error);
      throw error;
    }
  },

  // Send message to agent
  sendMessage: async (agentId, message) => {
    try {
      const response = await api.post(`/agents/${agentId}/message`, { message });
      return response.data;
    } catch (error) {
      console.error(`Failed to send message to ${agentId}:`, error);
      throw error;
    }
  },

  // Get agent logs
  getLogs: async (agentId) => {
    try {
      const response = await api.get(`/logs/${agentId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch logs for ${agentId}:`, error);
      throw error;
    }
  },
};

// Communication API functions
export const communicationAPI = {
  // Send search command (?find)
  search: async (query, fromAgent) => {
    try {
      const response = await api.post('/search', {
        message: query,
        fromAgent: fromAgent,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to execute search:', error);
      throw error;
    }
  },

  // Send A2A message (@agent)
  sendA2A: async (message, fromAgent, toAgent) => {
    try {
      const response = await api.post('/a2a', {
        message: message,
        fromAgent: fromAgent,
        toAgent: toAgent,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to send A2A message:', error);
      throw error;
    }
  },
};

// Messages API functions
export const messagesAPI = {
  // Get recent messages
  getMessages: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.agent_id) params.append('agent_id', filters.agent_id);
      if (filters.type) params.append('type', filters.type);
      
      const response = await api.get(`/messages?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      throw error;
    }
  },

  // Get message statistics
  getStats: async () => {
    try {
      const response = await api.get('/messages/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch message stats:', error);
      throw error;
    }
  },
};

// System API functions
export const systemAPI = {
  // Health check
  getHealth: async () => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Failed to get system health:', error);
      throw error;
    }
  },
};

// WebSocket connection for real-time updates
export class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  connect() {
    // Use relative WebSocket URL in production
    const wsUrl = process.env.REACT_APP_WS_URL || `wss://${window.location.host}/socket.io/`;
    
    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.emit('connected', { timestamp: new Date().toISOString() });
      };
      
      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message:', data);
          this.emit(data.type || 'message', data);
        } catch (error) {
          console.error('WebSocket message parse error:', error);
        }
      };
      
      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.emit('disconnected', { timestamp: new Date().toISOString() });
        this.handleReconnect();
      };
      
      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', { error: error.message, timestamp: new Date().toISOString() });
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in WebSocket event handler for ${event}:`, error);
        }
      });
    }
  }

  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.pow(2, this.reconnectAttempts) * 1000; // Exponential backoff
      
      console.log(`Attempting to reconnect WebSocket in ${delay}ms (attempt ${this.reconnectAttempts})`);
      
      setTimeout(() => {
        this.connect();
      }, delay);
    } else {
      console.error('Max WebSocket reconnection attempts reached');
      this.emit('reconnect_failed', { attempts: this.reconnectAttempts });
    }
  }
}

// Create singleton WebSocket service instance
export const wsService = new WebSocketService();

// Default export
export default api;
